/**
 * Training Season Settings (admin)
 *
 * Content of the "Training Season" tab on the admin Configuration page.
 * Sets the season start and end dates (defaults July 1 / November 1). These drive
 * the computed "current week" shown on the dashboard and Training page:
 *   - Week 1 is the (partial) week from the start date through the following Sunday.
 *   - Week 2 begins on the first Monday after the start date.
 *
 * Weekly plans themselves are created/edited on the Training page (/skills).
 */

import { useCallback, useEffect, useState } from 'react';
import { restClient } from '../../../api/rest-client';
import { Button } from '../../common/Button';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { useUIStore } from '../../../store/uiStore';

function formatLongDate(iso: string): string {
  // Parse as a local date (avoid TZ shift from `new Date('YYYY-MM-DD')` UTC parsing)
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Build the week schedule: each week number with the Monday that starts it.
 * Week 1's Monday is the Monday of the week containing the start date (e.g. a
 * Wed Jul 1 start -> Mon Jun 29); each subsequent week is +7 days.
 */
function buildWeekSchedule(startIso: string, weeks: number): { week: number; monday: string }[] {
  const [y, m, d] = startIso.split('-').map(Number);
  if (!y || !m || !d || weeks < 1) return [];
  const start = new Date(y, m - 1, d);
  const daysSinceMonday = (start.getDay() + 6) % 7; // JS: Sun=0..Sat=6 -> Mon=0
  const week1Monday = new Date(start);
  week1Monday.setDate(start.getDate() - daysSinceMonday);

  const out: { week: number; monday: string }[] = [];
  for (let n = 1; n <= weeks; n++) {
    const monday = new Date(week1Monday);
    monday.setDate(week1Monday.getDate() + (n - 1) * 7);
    out.push({
      week: n,
      monday: monday.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }),
    });
  }
  return out;
}

export function TrainingSeasonSettings() {
  const showToast = useUIStore((s) => s.showToast);

  const [season, setSeason] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [savedStart, setSavedStart] = useState<string>('');
  const [savedEnd, setSavedEnd] = useState<string>('');
  const [currentWeek, setCurrentWeek] = useState<number>(1);
  const [totalWeeks, setTotalWeeks] = useState<number>(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await restClient.getTrainingPlans();
      setSeason(data.season);
      setStartDate(data.start_date);
      setEndDate(data.end_date);
      setSavedStart(data.start_date);
      setSavedEnd(data.end_date);
      setCurrentWeek(data.current_week);
      setTotalWeeks(data.total_weeks);
    } catch (err) {
      console.error('Failed to fetch training season settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load training season settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const dirty = startDate !== savedStart || endDate !== savedEnd;
  const invalidRange = !!startDate && !!endDate && endDate <= startDate;

  const handleSave = useCallback(async () => {
    if (!startDate || !endDate) {
      showToast('error', 'Both a start date and an end date are required');
      return;
    }
    if (endDate <= startDate) {
      showToast('error', 'End date must be after the start date');
      return;
    }
    setSaving(true);
    try {
      await restClient.setTrainingSeasonDates({
        season: season || undefined,
        start_date: startDate,
        end_date: endDate,
      });
      // Refetch so the computed current week / total weeks update
      await fetchData();
      showToast('success', 'Season dates saved');
    } catch (err) {
      console.error('Failed to save season dates:', err);
      showToast('error', err instanceof Error ? err.message : 'Failed to save season dates');
    } finally {
      setSaving(false);
    }
  }, [startDate, endDate, season, fetchData, showToast]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="mb-6 flex items-center gap-3">
        <h2 className="text-2xl font-bold text-gray-900">Training Season</h2>
        {season && <span className="text-sm text-gray-500">Season {season}</span>}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <p className="mb-4 text-sm text-gray-600">
        Set the season window. Week 1 is the partial week from the start date through the
        following Sunday; week 2 begins on the first Monday after the start date. The current
        week is computed from these dates and shown on the dashboard and Training page.
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="season-start" className="block text-sm font-medium text-gray-700 mb-1">
            Season Start Date
          </label>
          <input
            type="date"
            id="season-start"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Season start date"
          />
        </div>
        <div>
          <label htmlFor="season-end" className="block text-sm font-medium text-gray-700 mb-1">
            Season End Date
          </label>
          <input
            type="date"
            id="season-end"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              invalidRange ? 'border-red-500' : 'border-gray-300'
            }`}
            aria-label="Season end date"
          />
        </div>
      </div>

      {invalidRange && (
        <p className="mt-2 text-sm text-red-600">End date must be after the start date.</p>
      )}

      <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
        <p>
          <span className="font-medium">Current week:</span> {currentWeek} of {totalWeeks}
        </p>
        {savedStart && (
          <p className="mt-1 text-gray-500">
            Season runs {formatLongDate(savedStart)} → {formatLongDate(savedEnd)}
          </p>
        )}
      </div>

      {/* Week schedule — each week's starting Monday */}
      {savedStart && totalWeeks > 0 && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-4 py-2">
            <p className="text-sm font-medium text-gray-700">Week schedule</p>
          </div>
          <ul className="max-h-80 divide-y divide-gray-100 overflow-y-auto">
            {buildWeekSchedule(savedStart, totalWeeks).map(({ week, monday }) => (
              <li
                key={week}
                className={`flex items-center justify-between gap-3 px-4 py-2 text-sm ${
                  week === currentWeek ? 'bg-primary-50 font-medium text-primary-800' : 'text-gray-700'
                }`}
              >
                <span>
                  Week {week}
                  {week === currentWeek && ' (current)'}
                </span>
                <span className="text-gray-500">{monday}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving || !dirty || invalidRange}
          aria-label="Save season dates"
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
